import { ref, computed, toRef, watch, toRaw } from 'vue'
import { fromObject } from '../../../helpers/dot'

export default function createSteps(initialStages, validate, form, errors) {

    const hasErrors = (fieldNames) => {
        return fieldNames.some((fieldName) => errors.value[fieldName] !== undefined)
    }

    const stages = Object.keys(initialStages)
        .reduce(
            (stages, stageName) => ({
                ...stages,
                [stageName]: initialStages[stageName](validate, form),
            }),
            {}
        )

    const currentStage = ref(Object.keys(stages)[0])
    const highestStage = ref(Object.keys(stages)[0])

    const activeStage = computed(() => stages[currentStage.value])

    const availableStages = computed(
        () => Object.keys(stages)
            .reduce((availableStages, stageName) => {
                if (stages[stageName].visible === undefined || stages[stageName].visible.value) {
                    availableStages[stageName] = stages[stageName]
                }

                return availableStages
            }, {})
    )

    const activatedStages = ref([])

    const getStageIndexFromName = (stageName) => Object.keys(availableStages.value).indexOf(stageName)

    const getStageNameFromIndex = (stageIndex) => Object.keys(availableStages.value)[stageIndex]

    const getPreviousStageName = (stageName) => getStageNameFromIndex(
        getStageIndexFromName(stageName) - 1
    )

    const getNextStageName = (stageName) => getStageNameFromIndex(
        getStageIndexFromName(stageName) + 1
    )

    const getStage = name => stages[name]

    const isValid = (stageName) => getStage(stageName).isValid ?? computed(() => true)

    const isValidUntil = (stageName) => {
        const stageIndex = getStageIndexFromName(stageName)

        return stageIndex === 0
            ? true
            : Array(stageIndex).fill()
                .every((_, stageIndex) => isValid(getStageNameFromIndex(stageIndex)).value)
    }

    /**
    * Checks if the section is valid based on the server response
    * 
    * @param {String} tabName 
    * @returns {Boolean}
    */
    const isValidServerSide = (stageName) => {
        const section = getStage(stageName)

        if (section?.isValidServerSide instanceof Function) {
            return section?.isValidServerSide()
        }

        if (section?.data !== undefined) {
            const fields = Object.keys(
                fromObject(section.data.value)
            )

            return !hasErrors(fields)
        }

        return true;
    }

    /**
     * Marks a stage as having been activated
     * 
     * @param {String} activatedStage 
     */
    const setStageActivated = (activatedStage) => {
        if (activatedStages.value.indexOf(activatedStage) === -1) {
            activatedStages.value.push(activatedStage)
        }
    }

    watch(
        currentStage,
        setStageActivated,
        { immediate: true }
    )

    return {
        /**
         * The stages for the accordion
         */
        stages: stages,

        /**
         * The current stage for the accordion
         */
        current_stage: currentStage,

        /**
         * The highest stage the accordion has reached
         */
        highest_stage: highestStage,

        /**
         * The currently active stage
         */
        activeStage: activeStage,

        /**
         * All stages which have been activated (accordions opened)
         */
        activatedStages: activatedStages,

        /**
         * Gets a given stage by name
         */
        stage: getStage,

        /**
         * Gets a stage index from its name
         */
        getStageIndexFromName: getStageIndexFromName,

        /**
         * Gets a stages name from its index
         */
        getStageNameFromIndex: getStageNameFromIndex,

        /**
         * Gets the prior stages name
         */
        getPreviousStageName: getPreviousStageName,

        /**
         * Checks if we can activate a given stage of the form
         * 
         * Validates is every stage is valid up until the current one
         * 
         * @param {String} stageName 
         * @returns {Boolean}
         */
        canActivateStage: (stageName) => {
            const stageIndex = getStageIndexFromName(stageName)
            const activeStageIndex = getStageIndexFromName(currentStage.value)

            //we can active the tab if its before the current step or all previous steps are valid
            //the use of less than means we can't close the current tab
            //this is intentional so we can't have no open tabs
            return stageIndex < activeStageIndex
                || isValidUntil(stageName)
        },

        /**
         * Checks if we have activated a given stage
         * 
         * @param {String} stageName 
         * @returns {Boolean}
         */
        hasActivatedStage: (stageName) => {
            return activatedStages.value.indexOf(stageName) !== -1
        },

        /**
         * Marks a stage as having been activated
         */
        setStageActivated: setStageActivated,

        /**
         * Checks if the current step is valid
         * 
         * @param {String} stageName 
         * @returns {Computed}
         */
        isValid: isValid,

        /**
         * Checks if the form is valid until the given step
         */
        isValidUntil: isValidUntil,

        /**
         * Checks if the form is valid from the backends perspective
         */
        isValidServerSide: isValidServerSide,

        /**
         * If the current stage is the first stage
         */
        isFirstStage: computed(
            () => getStageIndexFromName(currentStage.value) === 0
        ),

        /**
         * If the current stage is the final one
         */
        isLastStage: computed(
            () => getStageIndexFromName(currentStage.value) === Object.values(availableStages.value).length - 1
        ),

        /**
         * The stage immediately before the current stage
         */
        previousStageName: computed(
            () => getPreviousStageName(currentStage.value)
        ),

        /**
         * The stage immediately after the current stage
         */
        nextStageName: computed(
            () => getNextStageName(currentStage.value)
        ),

        previousStep: () => {
            currentStage.value = getPreviousStageName(currentStage.value)
        },

        nextStep: () => {
            currentStage.value = getNextStageName(currentStage.value)
        },

        availableStages: availableStages

        /**
         * @TODO:
         *  canActivateStep <-
         *  isValid, <-
         *  isValidServerSide
         *  isFirstStep <-
         *  isLastStep <-
         *  previousSectionName <-
         *  nextSectionName <-
         */
    }
}